<script setup lang="ts">
import type { ErrorObject } from "@vuelidate/core";
import type { BatchDocumentLinkResponse, TemplatesModel } from "@simbelapp/order-sdk";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import NotificationsArea from "~/components/Common/Notifications/NotificationsArea.vue";
import NotificationsDocuments from "~/components/Pages/notifications/NotificationsDocuments.vue";
import NotificationsDropddownList from "~/components/Pages/notifications/NotificationsDropdownList.vue";
import NotificationsText from "~/components/Common/Notifications/NotificationsText.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import { useAuthStore } from "~/store/auth/auth.store";
import { useNotificationsStore } from "~/store/notifications/notifications.store";
import { useSessionDetailsStore } from "~/store/sessions/session-details-store";

/**
 * Template, subject, body and attachments, shared by every session send form. Each form keeps its own
 * recipients and send.
 */
const props = withDefaults(
  defineProps<{
    subjectError?: ErrorObject;
    /** Read-only text shown before the subject. */
    subjectPrefix?: string | null;
    /** Subject used when the applied template carries none. */
    fallbackSubject?: string | null;
    /** Tooltip next to « Contenu du mail ». */
    contentHint?: string | null;
    showSubject?: boolean;
    showContent?: boolean;
  }>(),
  {
    subjectError: undefined,
    subjectPrefix: null,
    fallbackSubject: null,
    contentHint: null,
    showSubject: true,
    showContent: true,
  },
);

const emit = defineEmits<{
  openDocuments: [];
  refreshDocuments: [];
}>();

const authStore = useAuthStore();
const notificationStore = useNotificationsStore();
const { createSession, listTemplates, showDocumentModal } = storeToRefs(notificationStore);
const { sessionDetails } = storeToRefs(useSessionDetailsStore());

const chosenTemplate = ref<TemplatesModel | null>(null);

/** Only the latest fill is written: an earlier pick answered late must not land over it. */
let latestFill = 0;

/** Fills the template for this session and writes it over the subject and body. */
async function applyTemplate(template: TemplatesModel | null) {
  if (!template?.templateId || !sessionDetails.value?.id) return;

  const fill = ++latestFill;
  const filled = await notificationStore.getFilledTemplate(
    template.templateId,
    sessionDetails.value.id,
    sessionDetails.value.itemType,
  );
  if (!filled || fill !== latestFill) return;

  notificationStore.templateById = filled;
  chosenTemplate.value = filled;
  createSession.value.content = filled.content || "";
  createSession.value.subject = filled.subject || props.fallbackSubject || "";
}

/** Re-reads the list alongside the fill: « Paramétrer » edits the templates in another tab. */
async function selectTemplate(template: TemplatesModel | null) {
  await Promise.all([notificationStore.getTemplates(sessionDetails.value?.itemType), applyTemplate(template)]);
}

/** Called by the parent once the session it sends for is loaded. */
async function applyDefaultTemplate() {
  await notificationStore.getTemplates(sessionDetails.value?.itemType);
  const defaultTemplate = listTemplates.value.find((template: TemplatesModel) => template.isDefault);
  if (defaultTemplate) await applyTemplate(defaultTemplate);
}

function openTemplateSettings() {
  navigateTo("settings?section=notifications", { open: { target: "_blank" } });
}

/** Attached entries hold no file name: it is read back from the session's documents. */
const attachedDocuments = computed(() =>
  (createSession.value.batchDocuments ?? []).map((attached) => ({
    attached,
    fileName: createSession.value.documents?.find((document) => document.documentId === attached.documentId)?.fileName,
  })),
);

function openDocuments() {
  showDocumentModal.value = true;
  emit("openDocuments");
}

function closeDocuments() {
  showDocumentModal.value = false;
}

/**
 * Matched by id: the picker's selected rows and the attached entries are distinct objects. An
 * attached entry also carries the link minted for the participants' events.
 */
function removeDocument(attached: BatchDocumentLinkResponse) {
  const isKept = (document: { documentId: string }) => document.documentId !== attached.documentId;

  createSession.value.batchDocuments = createSession.value.batchDocuments?.filter(isKept);
  createSession.value.selectedDocuments = createSession.value.selectedDocuments?.filter(isKept);
  createSession.value.events.forEach((event) => {
    event.documents = event.documents.filter((link) => link !== attached.link);
  });
}

defineExpose({ applyDefaultTemplate });
</script>

<template>
  <div class="mb-[50px]">
    <div class="flex flex-col gap-5">
      <NotificationsDropddownList
        v-model="chosenTemplate"
        :options="listTemplates"
        placeholder="Choisir le modèle"
        track-by="templateId"
        @update:model-value="selectTemplate"
      >
        <template #header>
          <div class="flex justify-between mb-2">
            <div class="flex gap-1 items-center">
              <h5 class="body_regular text-secondary-color">Modèle de notifications</h5>
              <Tooltip v-if="!authStore.isCompanyAdmin" position="top-mid">
                <template #content>
                  <div class="flex w-[358px] gap-[8px]">
                    <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
                    Contactez votre Administrateur pour modifier ou supprimer un modèle de notification.
                  </div>
                </template>
              </Tooltip>
            </div>
            <AppButton
              v-if="authStore.isCompanyAdmin"
              type="link"
              label="Paramétrer"
              icon="link"
              icon-size="w-[14px] h-[14px]"
              reverse-icon
              @click="openTemplateSettings"
            />
          </div>
        </template>
      </NotificationsDropddownList>

      <NotificationsText
        v-if="showSubject"
        v-model="createSession.subject"
        label="Sujet du mail *"
        :error="subjectError"
        :prefixe="subjectPrefix"
      />

      <NotificationsArea v-if="showContent" v-model="createSession.content" class="mb-5">
        <template #title>
          <div class="flex justify-between items-center">
            <div class="flex gap-1">
              <h5 class="body_regular text-secondary-color">Contenu du mail</h5>
              <Tooltip v-if="contentHint" position="top-mid" class="flex items-center">
                <template #content>
                  <div class="flex w-[414px] gap-[8px]">
                    <img src="/icons/emojis/eyes.svg" alt="" class="h-4 w-4" />
                    {{ contentHint }}
                  </div>
                </template>
              </Tooltip>
            </div>
            <AppButton
              type="link"
              label="Réinitialiser"
              icon="refresh"
              @click="applyTemplate(notificationStore.templateById)"
            />
          </div>
        </template>
      </NotificationsArea>
    </div>

    <div class="flex flex-col" :class="attachedDocuments.length ? 'gap-7' : 'gap-5'">
      <div class="flex flex-col gap-4">
        <AppButton
          type="link"
          label="Joindre des documents"
          icon="attached-document"
          class="self-start"
          @click="openDocuments"
        />
        <div
          v-for="{ attached, fileName } in attachedDocuments"
          :key="attached.documentId"
          class="flex gap-2 items-center"
        >
          <SvgIcon class="svg_icon text-primary-blue-color" name="complete" />
          <span class="body_regular w-full">{{ fileName }}</span>
          <SvgIcon
            class="w-4 h-4 shrink-0 text-secondary-color cursor-pointer"
            name="close"
            @click="removeDocument(attached)"
          />
        </div>
      </div>
      <slot name="options" />
    </div>

    <NotificationsDocuments
      v-if="showDocumentModal"
      @hide="closeDocuments"
      @refresh-documents="emit('refreshDocuments')"
    />
  </div>
</template>
